import { Controller } from "@hotwired/stimulus"

const COLOR_HEX = {
  gray:   "#9ca3af",
  blue:   "#3b82f6",
  violet: "#8b5cf6",
  amber:  "#f59e0b",
  green:  "#22c55e",
  red:    "#ef4444",
  orange: "#f97316",
  pink:   "#f472b4"
}

export default class extends Controller {
  static targets = ["nativeSelect", "selectedDisplay", "selectedDot", "selectedLabel",
                    "search", "dropdown", "list",
                    "dialog", "form", "formErrors", "nameInput", "colorPicker"]

  connect() {
    this.allOptions = Array.from(this.nativeSelectTarget.options).map(opt => ({
      value: opt.value,
      label: opt.text,
      color: opt.dataset.color || "gray"
    }))

    const selected = this.allOptions.find(o => o.value === this.nativeSelectTarget.value)
    if (selected) this._setDisplay(selected)

    this._renderList(this.allOptions)
    this._outsideClick = this._onOutsideClick.bind(this)
  }

  toggle(event) {
    event.stopPropagation()
    this._isOpen() ? this.close() : this.open()
  }

  open() {
    this.selectedDisplayTarget.classList.add("hidden")
    this.searchTarget.classList.remove("hidden")
    this.dropdownTarget.classList.remove("hidden")
    this.searchTarget.value = ""
    this._renderList(this.allOptions)
    this.searchTarget.focus()
    document.addEventListener("click", this._outsideClick)
  }

  close() {
    this.dropdownTarget.classList.add("hidden")
    this.searchTarget.classList.add("hidden")
    this.selectedDisplayTarget.classList.remove("hidden")
    this.searchTarget.value = ""
    document.removeEventListener("click", this._outsideClick)
  }

  filter() {
    const q = this.searchTarget.value.toLowerCase()
    const filtered = q ? this.allOptions.filter(o => o.label.toLowerCase().includes(q)) : this.allOptions
    this._renderList(filtered)
  }

  select(event) {
    const li = event.currentTarget
    const opt = {
      value: li.dataset.value,
      label: li.dataset.label,
      color: li.dataset.color
    }
    this.nativeSelectTarget.value = opt.value
    this._setDisplay(opt)
    this.close()
  }

  openDialog() {
    this.dialogTarget.showModal()
  }

  closeDialog() {
    this.dialogTarget.close()
    this.nameInputTarget.value = ""
    const firstRadio = this.colorPickerTarget.querySelector('input[type="radio"]')
    if (firstRadio) firstRadio.checked = true
    this.formErrorsTarget.classList.add("hidden")
    this.formErrorsTarget.querySelector("ul").replaceChildren()
  }

  closeOnBackdrop(event) {
    if (event.target === this.dialogTarget) this.closeDialog()
  }

  validateName() {
    const name = this.nameInputTarget.value.trim().toLowerCase()
    const duplicate = name && this.allOptions.some(o => o.label.toLowerCase() === name)
    const errorsEl = this.formErrorsTarget
    const errList = errorsEl.querySelector("ul")
    errList.replaceChildren()
    if (duplicate) {
      const li = document.createElement("li")
      li.textContent = this.nameInputTarget.dataset.takenMessage || "Name has already been taken"
      errList.appendChild(li)
      errorsEl.classList.remove("hidden")
    } else {
      errorsEl.classList.add("hidden")
    }
  }

  async createStatus() {
    const name = this.nameInputTarget.value.trim()
    const color = this.colorPickerTarget.querySelector('input[type="radio"]:checked')?.value || "gray"
    const errorsEl = this.formErrorsTarget
    const errList = errorsEl.querySelector("ul")

    errorsEl.classList.add("hidden")
    errList.replaceChildren()

    if (!name) {
      const li = document.createElement("li")
      li.textContent = "Name can't be blank"
      errList.appendChild(li)
      errorsEl.classList.remove("hidden")
      return
    }

    if (this.allOptions.some(o => o.label.toLowerCase() === name.toLowerCase())) {
      errorsEl.classList.remove("hidden")
      return
    }

    const meta = document.querySelector('meta[name="csrf-token"]')
    const token = meta ? meta.content : ""

    try {
      const locale = new URLSearchParams(window.location.search).get("locale") || ""
      const url = locale ? `/issue_statuses?locale=${encodeURIComponent(locale)}` : "/issue_statuses"
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "Accept": "application/json"
        },
        body: JSON.stringify({ issue_status: { name, color }, source: "issue_form" })
      })

      const data = await res.json()

      if (res.ok) {
        const newOpt = { value: String(data.id), label: data.name, color: data.color }
        this.allOptions.push(newOpt)

        const option = document.createElement("option")
        option.value = newOpt.value
        option.text = newOpt.label
        option.dataset.color = newOpt.color
        option.selected = true
        this.nativeSelectTarget.appendChild(option)
        this.nativeSelectTarget.value = newOpt.value

        this._setDisplay(newOpt)
        this._renderList(this.allOptions)
        this.closeDialog()
      } else {
        const msgs = data.errors || [data.error || "Failed to save"]
        msgs.forEach(msg => {
          const li = document.createElement("li")
          li.textContent = msg
          errList.appendChild(li)
        })
        errorsEl.classList.remove("hidden")
      }
    } catch {
      const li = document.createElement("li")
      li.textContent = "Network error. Please try again."
      errList.appendChild(li)
      errorsEl.classList.remove("hidden")
    }
  }

  _isOpen() {
    return !this.dropdownTarget.classList.contains("hidden")
  }

  _setDisplay(opt) {
    this.selectedDotTarget.style.background = COLOR_HEX[opt.color] || COLOR_HEX.gray
    this.selectedLabelTarget.textContent = opt.label
    this.selectedLabelTarget.classList.remove("text-gray-400", "dark:text-gray-500")
    this.selectedLabelTarget.classList.add("text-gray-900", "dark:text-gray-100")
  }

  _renderList(options) {
    this.listTarget.replaceChildren()
    options.forEach(opt => {
      const li = document.createElement("li")
      li.dataset.value = opt.value
      li.dataset.label = opt.label
      li.dataset.color = opt.color
      li.dataset.action = "click->status-select#select"
      li.className = "flex items-center gap-2 px-3 py-2 text-sm text-gray-800 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 cursor-pointer"

      const dot = document.createElement("span")
      dot.className = "w-2.5 h-2.5 rounded-full shrink-0"
      dot.style.background = COLOR_HEX[opt.color] || COLOR_HEX.gray

      const label = document.createElement("span")
      label.textContent = opt.label

      li.append(dot, label)
      this.listTarget.appendChild(li)
    })

    if (options.length === 0) {
      const empty = document.createElement("li")
      empty.className = "px-3 py-2 text-sm text-gray-400 dark:text-gray-500"
      empty.textContent = "No results"
      this.listTarget.appendChild(empty)
    }
  }

  _onOutsideClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }
}
